/**
 * FeedbackAnalyticsSection — admin-only feedback review tab. Lists every
 * submitted feedback row with creator, resolver, department and remark.
 * Filters: date range, department, satisfaction.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { analyticsAPI } from '@/lib/api';
import { Download, RefreshCw, ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

const downloadFeedbackCsv = (rows) => {
  const headers = [
    'Ticket ID', 'Submitted At', 'Creator', 'Creator Email', 'Creator Role',
    'Department', 'Resolver', 'Resolver Email', 'Satisfaction', 'Remark',
  ];
  const lines = [headers.join(',')];
  rows.forEach((r) => {
    lines.push([
      r.ticket_id, fmtDate(r.feedback_submitted_at), r.creator_name,
      r.creator_email, r.creator_role, r.department, r.resolver_name,
      r.resolver_email, r.is_satisfactory ? 'Satisfied' : 'Not Satisfied',
      r.feedback_remark,
    ].map(csvEscape).join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `feedback-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const FeedbackAnalyticsSection = ({ departments = [] }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ feedback: [], satisfied: 0, not_satisfied: 0, count: 0 });
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    department: 'all',
    satisfaction: 'all',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.department !== 'all') params.department = filters.department;
      if (filters.satisfaction !== 'all') params.satisfaction = filters.satisfaction;
      const res = await analyticsAPI.getFeedbackAnalytics(params);
      setData(res.data || { feedback: [], satisfied: 0, not_satisfied: 0, count: 0 });
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load feedback right now.');
      if (process.env.NODE_ENV !== 'production') {
        console.debug('FeedbackAnalyticsSection load failed:', e?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const rows = data.feedback || [];

  return (
    <div className="space-y-4" data-testid="feedback-analytics-section">
      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400">Total Feedback</p>
              <p className="text-2xl font-bold text-white" data-testid="fb-total">{data.count}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-[#E97832] opacity-70" />
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-emerald-300">Satisfied</p>
              <p className="text-2xl font-bold text-white" data-testid="fb-satisfied">{data.satisfied}</p>
            </div>
            <ThumbsUp className="w-8 h-8 text-emerald-400 opacity-80" />
          </CardContent>
        </Card>
        <Card className="bg-rose-500/10 border-rose-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-rose-300">Not Satisfied</p>
              <p className="text-2xl font-bold text-white" data-testid="fb-not-satisfied">{data.not_satisfied}</p>
            </div>
            <ThumbsDown className="w-8 h-8 text-rose-400 opacity-80" />
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-slate-300 text-xs">From</Label>
            <Input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
              data-testid="fb-from-date"
              className="bg-slate-900 border-slate-700 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">To</Label>
            <Input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
              data-testid="fb-to-date"
              className="bg-slate-900 border-slate-700 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Department</Label>
            <Select value={filters.department} onValueChange={(v) => setFilters((f) => ({ ...f, department: v }))}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="fb-dept-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Satisfaction</Label>
            <Select value={filters.satisfaction} onValueChange={(v) => setFilters((f) => ({ ...f, satisfaction: v }))}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="fb-sat-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="satisfied">Satisfied</SelectItem>
                <SelectItem value="not_satisfied">Not Satisfied</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="fb-refresh-btn"
              className="border-slate-600 text-slate-200 bg-transparent hover:bg-slate-700 flex-1"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => downloadFeedbackCsv(rows)}
              disabled={loading || rows.length === 0}
              data-testid="fb-csv-btn"
              className="bg-[#E97832] hover:bg-[#d56b2c]"
            >
              <Download className="w-3.5 h-3.5 mr-2" />
              CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="text-amber-300 text-sm border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* Feedback table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-auto rounded-md max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 sticky top-0 z-10">
                <tr className="text-left text-slate-300">
                  <th className="px-3 py-2 font-medium">Submitted</th>
                  <th className="px-3 py-2 font-medium">Ticket</th>
                  <th className="px-3 py-2 font-medium">Creator</th>
                  <th className="px-3 py-2 font-medium">Department</th>
                  <th className="px-3 py-2 font-medium">Resolver</th>
                  <th className="px-3 py-2 font-medium">Result</th>
                  <th className="px-3 py-2 font-medium">Remark</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Loading feedback…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No feedback submissions yet for the selected filters.</td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr
                    key={r.ticket_id}
                    data-testid={`fb-row-${r.ticket_id}`}
                    className="border-t border-slate-700 hover:bg-slate-900/40"
                  >
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtDate(r.feedback_submitted_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-200">{r.ticket_id}</td>
                    <td className="px-3 py-2">
                      <div className="text-slate-100">{r.creator_name || '—'}</div>
                      <div className="text-xs text-slate-500">{r.creator_email}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-200">{r.department}</td>
                    <td className="px-3 py-2">
                      <div className="text-slate-100">{r.resolver_name}</div>
                      <div className="text-xs text-slate-500">{r.resolver_email}</div>
                    </td>
                    <td className="px-3 py-2">
                      {r.is_satisfactory ? (
                        <Badge className="bg-emerald-600 text-white font-semibold">
                          <ThumbsUp className="w-3 h-3 mr-1" /> Satisfied
                        </Badge>
                      ) : (
                        <Badge className="bg-rose-600 text-white font-semibold">
                          <ThumbsDown className="w-3 h-3 mr-1" /> Not Satisfied
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-200 max-w-[24rem]">
                      <span className="line-clamp-3">{r.feedback_remark || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackAnalyticsSection;
